import { Field, Form, useFormikContext } from 'formik'
import React, { useEffect } from 'react'

import { LeavingGuard } from '@components/utils/LeavingGuard'
import type { SelectOption } from '@fourthwall/components'
import { Banner, Fieldset, Input, Loading, Select, useDebounce } from '@fourthwall/components'

import { ConfirmBar } from '../../../../../../modules/ConfirmBar'
import type { SettingsFormValues } from '../../AppsShipStation.types'
import { isInitialApiSecret } from '../../utils/isInitialApiSecret'

type SettingsFormProps = {
  storesOptions: SelectOption[]
  storesWarning?: string
  initialApiSecret?: string
  isLoading: boolean
  isSubmitting: boolean
  onFetchStores: (values: SettingsFormValues) => void
}

const validateKey = (value: string) => {
  return /^[a-z0-9A-Z]{30,}$/i.test(value)
}

export const SettingsForm = ({
  storesOptions,
  storesWarning,
  isLoading,
  isSubmitting,
  onFetchStores,
}: SettingsFormProps) => {
  const settingsFormik = useFormikContext<SettingsFormValues>()
  const hasInitialApiSecret = isInitialApiSecret(settingsFormik.values.apiSecret)
  const areKeysValid =
    validateKey(settingsFormik.values.apiKey) &&
    (hasInitialApiSecret || validateKey(settingsFormik.values.apiSecret))
  const debouncedOnFetchStores = useDebounce(onFetchStores, 500)

  useEffect(() => {
    if (validateKey(settingsFormik.values.apiKey) && validateKey(settingsFormik.values.apiSecret)) {
      debouncedOnFetchStores(settingsFormik.values)
    }
  }, [settingsFormik.values.apiKey, settingsFormik.values.apiSecret])

  const handleApiKeyValueChange = () => {
    if (hasInitialApiSecret) {
      settingsFormik.setFieldValue('apiSecret', '')
    }
  }

  const storeSelectOptions = areKeysValid ? storesOptions : []

  return (
    <>
      <Form>
        <Fieldset>
          <Fieldset.Row>
            <Fieldset.Item>
              <Field
                component={Input}
                name="apiKey"
                label="API key"
                onValueChange={handleApiKeyValueChange}
              />
            </Fieldset.Item>
          </Fieldset.Row>
          <Fieldset.Row>
            <Fieldset.Item>
              <Field component={Input} name="apiSecret" label="API Secret" />
            </Fieldset.Item>
          </Fieldset.Row>
          {storesWarning && (
            <Fieldset.Row>
              <Fieldset.Item>
                <Banner appearance="warning">{storesWarning}</Banner>
              </Fieldset.Item>
            </Fieldset.Row>
          )}
          <Fieldset.Row>
            <Fieldset.Item>
              <Loading isLoading={isLoading} size="small">
                <Field
                  component={Select}
                  name="storeId"
                  options={storeSelectOptions}
                  label="ShipStation store"
                  disabled={!storeSelectOptions.length}
                  displayError={false}
                />
              </Loading>
            </Fieldset.Item>
          </Fieldset.Row>
        </Fieldset>
      </Form>
      <ConfirmBar
        isLoading={isSubmitting}
        isOpen={
          settingsFormik.dirty &&
          areKeysValid &&
          !!settingsFormik.values.storeId &&
          !!storeSelectOptions.length
        }
        onConfirm={settingsFormik.handleSubmit}
        onCancel={settingsFormik.resetForm}
      />
      <LeavingGuard
        when={settingsFormik.dirty}
        isValid={settingsFormik.isValid}
        onSave={settingsFormik.submitForm}
      />
    </>
  )
}
